import { Component, OnInit, OnDestroy } from '@angular/core';
import { WidgetCore } from '../../interfaces/widget.core.interface';
import { AnalyticsService } from '../../../analytics/services/analytics.service';
import {
	EVENT_EMITTERS,
	MAP_CONSTANTS,
	GAM_PRODUCT_SEPERATED_Rs
} from '../../../analytics/constants/analytics.constants';
import * as _ from 'underscore';
import { EmitterService } from '../../../../core/utility/services/emitter.service';
import { AnalyticsDataModel } from '../../../analytics/models/analytics.datamodel';

export interface MapOverviewStats {
	productId: number;
	productSelected: boolean;
	productName: string;
	productKey: string;
	impressions: number;
	clicks: number;
	audience: number;
	opens: number;
	hasClicks: boolean;
	ctr: number;
	completions: number;
	completions_rate: number;
	hasCtr: boolean;
	isEmail: boolean;
	isSocial: boolean;
	isExcludeInCount: boolean;
	isExclude: boolean;
	isDisabled: boolean;
	youtubeViews: number;
	youtubeViewRate: number;
}

@Component({
	selector: 'app-map-overview-stats',
	templateUrl: './map-overview-stats.component.html',
	styleUrls: ['./map-overview-stats.component.css']
})
export class MapOverviewStatsComponent implements OnInit, WidgetCore, OnDestroy {
	public key: string;
	public overviewStats: MapOverviewStats[] = [];
	public overviewYoutubeStats: MapOverviewStats[] = [];
	public reloadSubscription;
	public hasData: boolean;
	public widgetDataSubscription;
	public youtubeViewsPermission: boolean = false;
	public youtubeViewsPermissionCheck: object = { permission: { flags: ['hasYoutubeViews'] } };
	public isR2: boolean;
	public initiateMapStatsSubscription: any;
	public mapObj: any;
	constructor(protected analyticsService: AnalyticsService, protected analyticsDataModel: AnalyticsDataModel) {
		this.reloadSubscription = EmitterService.get(EVENT_EMITTERS.RELOAD).subscribe((response) => {
			this.requestData();
		});
		const validVersions = ['R2', 'R6'];
		this.initiateMapStatsSubscription =
			validVersions.includes(this.analyticsDataModel.config.version) &&
			EmitterService.get(EVENT_EMITTERS.INITIATE_MAP_STATS).subscribe((response: any) => {
				this.mapObj = response.legend;
				this.requestData();
			});
	}

	ngOnInit() {
		this.isR2 = this.analyticsDataModel.config.version === 'R2' ? true : false;
		// Moving requestData() for R2 to the constructor to ensure it's called only when INITIATE_MAP_STATS is ready.
		if (!this.isR2) {
			this.requestData();
		}
		this.youtubeViewsPermission = this.analyticsDataModel.checkComponentForPermission(this.youtubeViewsPermissionCheck);
	}

	ngOnDestroy() {
		if (this.reloadSubscription) {
			this.reloadSubscription.unsubscribe();
		}
		if (this.widgetDataSubscription) {
			this.widgetDataSubscription.unsubscribe();
		}
		if (this.initiateMapStatsSubscription) {
			this.initiateMapStatsSubscription.unsubscribe();
		}
	}

	requestData() {
		this.hasData = true;
		this.widgetDataSubscription = this.analyticsService.processOverviewRequest(this.key).subscribe((response: any) => {
			if (response.success) {
				this.hasData = true;
				this.processResponseData(response);
			} else {
				this.overviewStats = [];
				this.overviewYoutubeStats = [];
				this.hasData = false;
			}
		});
	}

	processResponseData(response: any) {
		const responseData = response.data;
		this.overviewStats = [];
		this.overviewYoutubeStats = [];
		this.mapResponseDataToInterface(responseData);
		this.toggleProductSelection(true);
	}

	mapResponseDataToInterface(responseData: any) {
		let count = 0;
		let tempPermissions = [];
		tempPermissions = this.analyticsDataModel.setupData.permissions;
		let gam_seperated = GAM_PRODUCT_SEPERATED_Rs.includes(this.analyticsDataModel.config.directoryName);
		if (!gam_seperated || (gam_seperated && !tempPermissions.includes('hasGamMapAccess'))) {
			responseData = responseData.filter((item) => item.product_name !== 'GAM');
		}
		for (const responseObj of responseData) {
			if (count !== responseData.length - 1) {
				const overviewStatsObj: MapOverviewStats = <MapOverviewStats>{};
				overviewStatsObj.productId = parseInt(responseObj.product_id);
				overviewStatsObj.productName = responseObj.product_name;
				overviewStatsObj.productSelected = true;
				overviewStatsObj.impressions = responseObj.impressions != null ? parseInt(responseObj.impressions) : 0;
				overviewStatsObj.clicks = responseObj.clicks != null ? parseInt(responseObj.clicks) : 0;
				overviewStatsObj.audience = responseObj.audience != null ? parseInt(responseObj.audience) : 0;
				overviewStatsObj.opens = responseObj.opens != null ? parseInt(responseObj.opens) : 0;
				overviewStatsObj.hasClicks = !(overviewStatsObj.productName === 'Streaming Audio');
				overviewStatsObj.ctr = responseObj.ctr != null ? parseFloat(responseObj.ctr) : 0;
				overviewStatsObj.completions = responseObj.completions != null ? parseInt(responseObj.completions) : 0;
				overviewStatsObj.completions_rate =
					responseObj.completions_rate != null ? parseFloat(responseObj.completions_rate) : 0;
				overviewStatsObj.hasCtr = !(overviewStatsObj.productName === 'Streaming Audio');
				overviewStatsObj.isExcludeInCount = !(overviewStatsObj.productName === 'Connected TV');
				overviewStatsObj.isExclude = count === responseData.length - 1;
				// Earlier, we were checking "overviewStatsObj.clicks === 0 && overviewStatsObj.impressions === 0;" for enabling the map legend.
				// Updating it for R2 to get the status of each product from "MapComponentService," which is more reliable.
				const productData = this.isR2
					? this.mapObj?.find((item) => {
							if (item.productName.toLowerCase() === 'ctv') {
								item.productName = 'Connected TV';
							}
							return item.productName === overviewStatsObj.productName;
					  })
					: null;
				if (productData && !productData.isDisabled) {
					productData.isDisabled = overviewStatsObj.clicks === 0 && overviewStatsObj.impressions === 0;
				}
				overviewStatsObj.isDisabled = productData ? productData.isDisabled : true;
				overviewStatsObj.productKey = this.getProductKeyByProductName(overviewStatsObj.productName);
				count++;
				this.overviewStats.push(overviewStatsObj);

				if (responseObj.product_name == 'Pre Roll' && this.isR2) {
					const overviewYoutubeStatsObj: MapOverviewStats = <MapOverviewStats>{};
					overviewYoutubeStatsObj.productId = overviewStatsObj.productId;
					overviewYoutubeStatsObj.youtubeViews =
						responseObj.youtube_verified_imprs != null ? responseObj.youtube_verified_imprs : 0;
					overviewYoutubeStatsObj.productName = 'YouTube';
					overviewYoutubeStatsObj.isDisabled = true;
					if (overviewYoutubeStatsObj.youtubeViews > 0) {
						overviewYoutubeStatsObj.productId = 0;
						overviewYoutubeStatsObj.productSelected = true;
						overviewYoutubeStatsObj.isExclude = false;
						overviewYoutubeStatsObj.isDisabled = this.isR2 ? true : false; //Disabling the YouTube product due to the absence of map data to display.
						overviewYoutubeStatsObj.productKey = this.getProductKeyByProductName(overviewYoutubeStatsObj.productName);
					}
					overviewYoutubeStatsObj.youtubeViewRate =
						overviewYoutubeStatsObj.youtubeViews > 0
							? (responseObj.youtube_verified_imprs / responseObj.youtube_imprs) * 100
							: 0;
					this.overviewYoutubeStats.push(overviewYoutubeStatsObj);
				}
			}
		}
	}

	showLiftDetails(selectedProducts) {
		return _.filter(selectedProducts, (product: any) => {
			return (
				(product.productName === 'Display' && product.productSelected) ||
				(product.productName === 'Pre Roll' && product.productSelected)
			);
		});
	}

	getProductKeyByProductName(productName: string): string {
		productName = productName.toLowerCase();
		if (productName == 'display') {
			return MAP_CONSTANTS.STATE.DISPLAY;
		} else if (productName === 'preroll' || productName === 'pre roll' || productName == 'video') {
			return MAP_CONSTANTS.STATE.PREROLL;
		} else if (productName === 'sem') {
			return MAP_CONSTANTS.STATE.SEM;
		} else if (
			productName === 'ctv' ||
			productName === 'connected tv' ||
			productName === 'connectedtv' ||
			productName === 'ctv/ott'
		) {
			return MAP_CONSTANTS.STATE.CTV;
		} else if (productName === 'social') {
			return MAP_CONSTANTS.STATE.SOCIAL;
		} else if (productName === 'geofence' || productName === 'geo fence') {
			return MAP_CONSTANTS.STATE.GEOFENCE;
		} else if (productName === 'geovideo' || productName === 'geo video' || productName == 'videogeofencing') {
			return MAP_CONSTANTS.STATE.GEOVIDEO;
		} else if (productName === 'radio' || productName == 'streaming audio' || productName == 'streamingaudio') {
			return MAP_CONSTANTS.STATE.RADIO;
		} else if (productName === 'youtube') {
			return MAP_CONSTANTS.STATE.YOUTUBE;
		} else if (productName === 'nexstar' || productName === 'gam' || productName === 'o&o') {
			return MAP_CONSTANTS.STATE.GAM;
		} else if (productName === 'email') {
			return MAP_CONSTANTS.STATE.EMAIL;
		}
		return '';
	}

	toggleProductSelection(onlyUpdate?) {
		const combinedOverviewStats = _.union(this.overviewStats, this.overviewYoutubeStats);
		const enabledProducts = _.where(combinedOverviewStats, { isDisabled: false });
		const excludedProducts = _.where(enabledProducts, { isExclude: false });
		const selectedProducts = _.where(excludedProducts, { productSelected: true });
		const removedProducts = _.where(excludedProducts, { productSelected: false });
		if (onlyUpdate) {
			EmitterService.get(EVENT_EMITTERS.UPDATE_PRODUCT_MAP_LEGEND).emit({
				selected: selectedProducts,
				removed: removedProducts
			});
		} else {
			EmitterService.get(EVENT_EMITTERS.TOGGLE_PRODUCT_MAP_LEGEND).emit({
				selected: selectedProducts,
				removed: removedProducts
			});
		}
	}

	getTotalByColumn(columnName: string) {
		const selectedStats = _.filter(this.overviewStats, (obj) => {
			return columnName === 'impressions' ? true : obj.productName !== 'Streaming Audio';
		});
		const columnValues: number[] = _.pluck(selectedStats, columnName);
		const total = _.reduce(
			columnValues,
			function (memo, num) {
				return memo + num;
			},
			0
		);
		return total;
	}

	getTotalCTR() {
		const totalImpressions = this.getTotalByColumn('impressions');
		const totalClicks = this.getTotalByColumn('clicks');
		return totalImpressions !== 0 ? totalClicks / totalImpressions : totalImpressions;
	}
	hasAnyData(stat: any): boolean {
		return (
			!this.isZeroImpression(stat.impressions) ||
			!this.isZeroImpression(stat.clicks) ||
			!this.isZeroImpression(stat.ctr)
		);
	}
	isZeroImpression(val: any): boolean {
		return val === 0 || val === '0.00' || val === 'N/A' || val == null;
	}
}
